import { useMutation, useQuery } from "@apollo/client";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CancelIcon from "@mui/icons-material/Cancel";
import Description from "@mui/icons-material/Description";
import EventIcon from "@mui/icons-material/Event";
import MoveUpIcon from "@mui/icons-material/MoveUp";
import ReplayIcon from "@mui/icons-material/Replay";
import { TabContext, TabPanel } from "@mui/lab";
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { SubNavAppBar } from "components/AppBar";
import { DataGrid } from "components/DataGrid";
import { Dialog } from "components/Dialog";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import CopyableTypography from "components/Typography/CopyableTypography";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { DateTime } from "luxon";
import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { prettyPrintDateTime } from "util/dates";
import { useAlerts } from "../../../../../components/Alerts/AlertProvider";
import { Filter } from "../../../../../components/Omnisearch/types";
import { SpinnerOverlay } from "../../../../../components/SpinnerOverlay";
import { gql } from "../../../../../generated-graphql";
import {
  ExtractionsWebAutomationJobsQuery,
  JobStatus,
} from "../../../../../generated-graphql/graphql";
import { useCurrentUser } from "../../../../../hooks/useCurrentUser";
import { useQueryParams } from "../../../../../hooks/useQueryParams";
import { JobLogs } from "./JobLogs";
import { T2sImportDialog } from "./T2sImportDialog";

type Row = ExtractionsWebAutomationJobsQuery["webAutomationJobs"]["items"][0];

const JOBS_QUERY = gql(`
  query ExtractionsWebAutomationJobs($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    webAutomationJobs(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        action,
        reportingYear
        status,
        stateOrTerritory
        tenantId,
        facilityId
        reportId
        extractionKey
        facility {
          name
          customerFacilityId
        }
        tenant {
          name
        }
        createdAt
        updatedAt
        promotedAt
        promotionJobId
        promotionJobStatus
        promotionJobUpdatedAt
      }
      count
    }
  }
`);

const JOB_WITH_EXTRACTION_QUERY = gql(`
  query ExtractionsJob($id: ID!) {
    webAutomationJob(id: $id) {
      id
      action,
      reportingYear
      status,
      stateOrTerritory
      tenantId,
      facilityId
      reportId
      extractionKey
      extraction
      facility {
        name
        customerFacilityId
      }
      tenant {
        name
      }
      createdAt
      updatedAt
      promotedAt
      promotionJobId
      promotionJobStatus
      promotionJobUpdatedAt
    }
  }
`);

const UPDATE_JOB_MUTATION = gql(`
  mutation UpdateWebAutomationJobs(
    $ids: [ID!]!
    $input: UpdateWebAutomationJobInput!
  ) {
    updateWebAutomationJobs(ids: $ids, input: $input) {
      count
    }
  }
`);

const PROMOTE_DRAFT_MUTATION = gql(`
  mutation PromoteDraftReport($webAutomationJobId: ID!) {
    promoteDraftReport(webAutomationJobId: $webAutomationJobId)
  }
`);

export function ExtractionsTabPanel({ tenantId }: { tenantId: string }) {
  const currentUser = useCurrentUser();
  const alerts = useAlerts();

  const webAutomationAction = "extractData";

  const currentUserEmail = currentUser.user?.email;

  const { filters } = useQueryParams<Filter>();

  const [logsJobId, setLogsJobId] = useState<string | null>(null);

  // scroll the data grid into view when the omnisearch filters change
  const dataGridRef = useRef<HTMLDivElement>(null);
  const [knownFilter, setKnownFilter] = useState<Partial<Filter>>({});

  const [isCancelExtractionsModalOpen, setIsCancelExtractionsModalOpen] =
    useState(false);
  const [isCancellingExtractions, setIsCancellingExtractions] = useState(false);

  const handleOpenCancelExtractionsModal = () => {
    setIsCancelExtractionsModalOpen(true);
  };

  const handleCloseCancelExtractionsModal = () => {
    setIsCancelExtractionsModalOpen(false);
  };

  const [t2sModalOpen, setT2sModalOpen] = useState(false);
  const [isRequeueExtractionsModalOpen, setIsRequeueExtractionsModalOpen] =
    useState(false);
  const [isRequeueingExtractions, setIsRequeueingExtractions] = useState(false);

  const handleOpenLoadFromT2SModal = () => {
    setT2sModalOpen(true);
  };

  const handleOpenRequeueExtractionsModal = () => {
    setIsRequeueExtractionsModalOpen(true);
  };

  const handleCloseRequeueExtractionsModal = () => {
    setIsRequeueExtractionsModalOpen(false);
  };

  useEffect(() => {
    // known filter prevents bouncing if you scroll after clicking, since useQueryParams
    // changes a few times, it seems like
    if (filters.omnisearch !== knownFilter.omnisearch) {
      setKnownFilter(filters);
      if (dataGridRef.current && filters.omnisearch) {
        dataGridRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [filters, setKnownFilter, knownFilter]);

  const [updateWebAutomationJob] = useMutation(UPDATE_JOB_MUTATION, {
    refetchQueries: ["ExtractionsWebAutomationJobs"],
    onCompleted: async () => {},
    onError: () => {
      alerts.error("Error Updating Extraction Statuses!");
    },
  });

  const handleCancelExtractions = async () => {
    setIsCancellingExtractions(true);
    await updateWebAutomationJob({
      variables: {
        ids: selectedCancellableRows.map((row) => row.id),
        input: {
          status: JobStatus.ShouldCancel,
        },
      },
    });
    await new Promise((res) => setTimeout(res, 1000));
    setIsCancellingExtractions(false);
    handleCloseCancelExtractionsModal();
    alerts.success("Successfully Cancelled Extractions!");
  };

  const handleRequeueExtractions = async () => {
    setIsRequeueingExtractions(true);
    await updateWebAutomationJob({
      variables: {
        ids: selectedRequeueableRows.map((row) => row.id),
        input: {
          status: JobStatus.Queued,
        },
      },
    });
    await new Promise((res) => setTimeout(res, 1000));
    setIsRequeueingExtractions(false);
    handleCloseRequeueExtractionsModal();
    alerts.success("Successfully Requeued Extractions!");
  };

  const [selectedExtractionId, setSelectedExtractionId] = useState<
    string | null
  >(null);

  const viewExtraction = useCallback((jobId: string) => {
    setSelectedExtractionId(jobId);
  }, []);

  const [selectedRows, setSelectedRows] = useState<Row[]>([]);

  // Doing this so the table in the cancel modal doesn't disappear
  // until the mutation is complete
  const selectedCancellableRowsPreviousValue = useRef<Row[]>([]);
  const selectedCancellableRows = useMemo(() => {
    if (isCancellingExtractions) {
      return selectedCancellableRowsPreviousValue.current;
    }

    const newValue = selectedRows.filter(
      (row) =>
        row.status === JobStatus.Running || row.status === JobStatus.Queued
    );

    selectedCancellableRowsPreviousValue.current = newValue;
    return newValue;
  }, [selectedRows, isCancellingExtractions]);

  const selectedRequeueableRowsPreviousValue = useRef<Row[]>([]);
  const selectedRequeueableRows = useMemo(() => {
    if (isRequeueingExtractions) {
      return selectedRequeueableRowsPreviousValue.current;
    }

    const newValue = selectedRows.filter(
      (row) =>
        row.status === JobStatus.Cancelled ||
        row.status === JobStatus.FailedQualityControlCheck ||
        row.status === JobStatus.FailedProvisioningEcsTask ||
        row.status === JobStatus.FailedNeedsHuman ||
        // Not sure if this should be requeueable
        row.status === JobStatus.FailedMayAutoRetry
    );

    selectedRequeueableRowsPreviousValue.current = newValue;
    return newValue;
  }, [selectedRows, isRequeueingExtractions]);

  if (!currentUserEmail) return <></>;

  const confirmationColumns: OmnisearchGridColDef<Row>[] = [
    {
      field: "tenant.name",
      headerName: "Organization",
      flex: 0.15,
      valueGetter: (_, row) => {
        return row.tenant?.name ?? "";
      },
    },
    {
      field: "facility",
      headerName: "Facility",
      filterKeyType: "facility",
      flex: 0.25,
      valueGetter: (_, row) => {
        return row.facility?.name ?? "";
      },
    },
    {
      field: "customerFacilityId",
      headerName: "Customer Facility ID",
      flex: 0.25,
      valueGetter: (_, row) => {
        return row.facility?.customerFacilityId ?? "";
      },
    },
    {
      field: "stateOrTerritory",
      headerName: "State",
      flex: 0.1,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.25,
      enumValues: Object.values(JobStatus),
      filterKeyType: "enum",
    },
    {
      field: "reportingYear",
      headerName: "Reporting Year",
      flex: 0.15,
      filterKeyType: "number",
    },
  ];

  const columns: OmnisearchGridColDef<Row>[] = [
    ...confirmationColumns,
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 0.2,
      valueFormatter: (_, row) =>
        row.createdAt ? prettyPrintDateTime(row.createdAt) : undefined,
      filterKeyType: "time",
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      flex: 0.2,
      valueFormatter: (_, row) =>
        row.updatedAt ? prettyPrintDateTime(row.updatedAt) : undefined,
      filterKeyType: "time",
    },
    {
      field: "promotedAt",
      headerName: "Promoted At",
      flex: 0.2,
      valueGetter: (_, row) => {
        return row.promotedAt ?? row.promotionJobUpdatedAt;
      },
      valueFormatter: (_, row) => {
        return row.promotedAt
          ? prettyPrintDateTime(row.promotedAt)
          : row.promotionJobUpdatedAt
          ? prettyPrintDateTime(row.promotionJobUpdatedAt)
          : undefined;
      },
      filterKeyType: "time",
    },
    {
      field: "view",
      type: "actions",
      getActions: (params) => [
        <Tooltip title="View Logs" key={0}>
          <GridActionsCellItem
            onClick={() => setLogsJobId(params.row.id)}
            label="view logs"
            icon={<Description />}
          />
        </Tooltip>,
        <Tooltip title="View details" key={1}>
          <GridActionsCellItem
            disabled={
              !(
                params.row.status === "SUCCEEDED" ||
                params.row.status === "AWAITING_MANUAL_INTERVENTION" ||
                // deprecated
                params.row.status === "PROMOTED"
              )
            }
            onClick={() => viewExtraction(params.row.id)}
            label="view extraction details"
            icon={<ArrowCircleRightIcon />}
          />
        </Tooltip>,
      ],
    },
  ];
  const canRequeueSelectedExtractions =
    isRequeueingExtractions || !!selectedRequeueableRows.length;
  const canCancelSelectedExtractions =
    isCancellingExtractions || !!selectedCancellableRows.length;
  return (
    <>
      <OmnisearchDataGrid
        persistenceKey="extractions"
        isURLDriven
        withPadding={false}
        dataQuery={JOBS_QUERY}
        getItems={(data) => data.webAutomationJobs.items}
        getCount={(data) => data.webAutomationJobs.count}
        defaultSearch={`tenantId:${tenantId} action:${webAutomationAction}`}
        isRowSelectable={(params) => params.row.status !== "SUCCEEDED"}
        onSelectedRowsChanged={setSelectedRows}
        columns={columns}
        initialSortModel={[{ field: "updatedAt", sort: "desc" }]}
        initialState={{
          pinnedColumns: {
            right: ["actions"],
          },
        }}
        commandButtons={[
          <Button
            key="t2s"
            variant="contained"
            color="primary"
            onClick={handleOpenLoadFromT2SModal}
          >
            Load data from T2S
          </Button>,
          <Button
            key="requeue"
            variant="contained"
            color="info"
            onClick={handleOpenRequeueExtractionsModal}
          >
            Requeue Extractions
          </Button>,
          <Button
            key="cancel"
            variant="contained"
            color="warning"
            onClick={handleOpenCancelExtractionsModal}
          >
            Cancel Extractions
          </Button>,
        ]}
      />
      {t2sModalOpen && (
        <T2sImportDialog
          open={t2sModalOpen}
          onClose={(event, reason) => {
            if (reason !== "backdropClick") {
              setT2sModalOpen(false);
            }
          }}
        />
      )}
      <Dialog
        fullWidth
        maxWidth="lg"
        open={isRequeueExtractionsModalOpen}
        onClose={handleCloseRequeueExtractionsModal}
        title={
          canRequeueSelectedExtractions
            ? "The Following Extractions Will Be Requeued"
            : "No Requeueable Extractions Selected"
        }
        subheader="Note: Only extractions with a FAILED (various types) or
       CANCELLED status can be Requeued"
        noPrimaryAction={!canRequeueSelectedExtractions as any}
        primaryActionButtonProps={
          canRequeueSelectedExtractions
            ? {
                loading: isRequeueingExtractions,
                startIcon: <ReplayIcon />,
                onClick: handleRequeueExtractions,
                saveText: "Confirm",
              }
            : undefined
        }
      >
        {canRequeueSelectedExtractions && (
          <DataGrid
            rows={selectedRequeueableRows}
            columns={confirmationColumns}
          />
        )}
      </Dialog>

      <Dialog
        fullWidth
        maxWidth="lg"
        open={isCancelExtractionsModalOpen}
        onClose={handleCloseCancelExtractionsModal}
        title={
          canCancelSelectedExtractions
            ? " The Following Extractions Will Be Cancelled"
            : "No Cancellable Extractions Selected"
        }
        subheader="Note: Only extractions with a status of RUNNING or QUEUED can be cancelled"
        noPrimaryAction={!canCancelSelectedExtractions as any}
        primaryActionButtonProps={
          canCancelSelectedExtractions
            ? {
                loading: isCancellingExtractions,
                startIcon: <CancelIcon />,
                onClick: handleCancelExtractions,
                saveText: "Confirm",
              }
            : undefined
        }
      >
        {canCancelSelectedExtractions && (
          <DataGrid
            rows={selectedCancellableRows}
            columns={confirmationColumns}
          />
        )}
      </Dialog>

      {selectedExtractionId && (
        <ExtractionDetail jobId={selectedExtractionId} />
      )}
      {logsJobId && (
        <Dialog
          open={!!logsJobId}
          title="Extraction Logs"
          onClose={() => setLogsJobId(null)}
          maxWidth="xl"
          fullWidth
          noPrimaryAction
        >
          <JobLogs id={logsJobId} />
        </Dialog>
      )}
    </>
  );
}

function ExtractionDetail({ jobId }: { jobId: string }) {
  const alerts = useAlerts();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tab, setTab] = useState("report");
  const [isPromotingExtraction, setIsPromotingExtraction] = useState(false);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleTabChange = useCallback(
    (_: SyntheticEvent, selectedTab: string) => setTab(selectedTab),
    [setTab]
  );

  const [promoteDraft] = useMutation(PROMOTE_DRAFT_MUTATION, {
    refetchQueries: ["ExtractionsWebAutomationJobs"],
    onCompleted: () => {
      alerts.success("Successfully Promoted Extraction!");
    },
    onError: (error) => {
      alerts.error(`Error promoting extraction! ${error.message}`);
    },
  });

  const handlePromoteExtraction = async (webAutomationJobId: string) => {
    setIsPromotingExtraction(true);
    await promoteDraft({ variables: { webAutomationJobId } });
    setIsPromotingExtraction(false);
    setIsModalOpen(false);
  };

  const { data, loading } = useQuery(JOB_WITH_EXTRACTION_QUERY, {
    variables: {
      id: jobId,
    },
    onCompleted: () => {
      window.scrollTo(0, document.body.scrollHeight);
    },
  });

  // disable promote button if the extraction is not succeeded, the promotion job is already created, or the extraction key is not set
  const disablePromoteButton =
    data?.webAutomationJob?.status !== "SUCCEEDED" ||
    !!data?.webAutomationJob?.promotionJobId ||
    !data?.webAutomationJob?.extractionKey;

  return (
    <Box
      style={{
        marginTop: "2rem",
      }}
    >
      <SpinnerOverlay loading={loading} />
      <Stack direction={"column"}>
        <Stack direction="row">
          <Typography variant="h6">
            {`${data?.webAutomationJob?.tenant?.name ?? ""} - ${
              data?.webAutomationJob?.facility?.name ?? ""
            } ${data?.webAutomationJob?.facility?.customerFacilityId ?? ""} - ${
              data?.webAutomationJob?.stateOrTerritory ?? ""
            }`}
          </Typography>
          <Box flexGrow={1} />
          <Button
            variant="contained"
            color="info"
            onClick={handleOpenModal}
            disabled={disablePromoteButton}
          >
            Promote
          </Button>
        </Stack>
        <Stack direction={"row"}>
          <List dense={true}>
            <ListItem>
              <ListItemIcon>
                <CalendarMonthIcon />
              </ListItemIcon>
              <ListItemText
                primary="Reporting Year"
                secondary={data?.webAutomationJob?.reportingYear}
              />
            </ListItem>
          </List>
          <List dense={true}>
            <ListItem>
              <ListItemIcon>
                <EventIcon />
              </ListItemIcon>
              <ListItemText
                primary="Started At"
                secondary={DateTime.fromISO(
                  data?.webAutomationJob?.createdAt
                ).toFormat("MM/dd/yy hh:mm a")}
              />
            </ListItem>
          </List>
          <List dense={true}>
            <ListItem sx={{ display: "block" }}>
              <ListItemText>Job Id</ListItemText>
              <CopyableTypography text={jobId} />
            </ListItem>
          </List>
        </Stack>
      </Stack>

      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        title="Are you sure?"
        subheader="Are you sure you want to promote this extraction?"
        primaryActionButtonProps={{
          loading: isPromotingExtraction,
          startIcon: <MoveUpIcon />,
          onClick: () => handlePromoteExtraction(jobId),
          saveText: "Confirm",
        }}
      />

      <TabContext value={tab}>
        <SubNavAppBar>
          <Tabs value={tab} onChange={handleTabChange}>
            <Tab label="Report" value="report"></Tab>
            <Tab label="Facility" value="facility"></Tab>
            <Tab label="Facility State Fields" value="state fields"></Tab>
            <Tab label="Contacts" value="contacts"></Tab>
            <Tab label="Products" value="products"></Tab>
            <Tab label="Documents" value="documents"></Tab>
          </Tabs>
        </SubNavAppBar>

        <ExtractionDetailTabPanel
          value="report"
          data={data?.webAutomationJob.extraction}
        />
        <ExtractionDetailTabPanel
          value="facility"
          data={data?.webAutomationJob.extraction.basic}
        />
        <ExtractionDetailTabPanel
          value="contacts"
          data={data?.webAutomationJob.extraction.contacts}
        />
        <ExtractionDetailTabPanel
          value="products"
          data={data?.webAutomationJob.extraction.products}
        />
        <ExtractionDetailTabPanel
          value="documents"
          data={data?.webAutomationJob.extraction.records}
        />
        <ExtractionDetailTabPanel
          value="state fields"
          data={data?.webAutomationJob.extraction.state?.fields}
        />
      </TabContext>
    </Box>
  );
}

const ExtractionDetailTabPanel = ({
  value,
  data,
}: {
  value: string;
  data: any;
}) => {
  return (
    <TabPanel value={value}>
      <Box style={{ maxHeight: "500px", overflowY: "scroll" }}>
        <pre>{JSON.stringify(data, null, 2)}</pre>
      </Box>
    </TabPanel>
  );
};
